import {
  FC,
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import styled, { css } from "styled-components";
import { Control, useController } from "react-hook-form";

import useDebounce from "@/hooks/useDebounce";
import { Input, Label, Larger } from "@/components/Forms";
import Text from "@/components/Text";
import Tag from "@/components/Tag";
import { api } from "@/utils/api";
import { VideoTopicResource } from "@/types/resources";
import { FormDataT } from "./ProjectModal";
import ValidationMessage from "@/components/Forms/ValidationMessage";

const InputWrap = styled.div`
  position: relative;
  ${Input} {
    padding-right: 65px;
  }
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
`;

const Autocomplete = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  border-radius: 4px;
  padding: 8px 4px;
  max-height: 368px;
  overflow-y: auto;
  left: 0;
  width: 100%;
  z-index: 1;
`;

const AutocompleteButton = styled.button`
  padding: 9px 12px;
  background-color: ${({ theme }) => theme.colors.white};
  transition: 0.2s ease-out all;
  line-height: 22px;
  width: 100%;
  text-align: left;
  border-radius: 4px;
  outline: none;
  &:hover,
  &:focus {
    background-color: ${({ theme }) => theme.colors.grayscaleLight};
    ${Text} {
      &:first-child {
        color: ${({ theme }) => theme.colors.black};
      }
    }
  }
  ${Text} {
    transition: color ease-out 0.2s;
    > span {
      color: ${({ theme }) => theme.colors.purple};
    }
  }
`;

interface AttachmentBoxProps {
  $isGray?: boolean;
}

const AttachmentBox = styled.div<AttachmentBoxProps>`
  ${({ $isGray }) =>
    $isGray &&
    css`
      margin: 32px 0;
    `}
`;

interface Props {
  control: Control<FormDataT>;
}

const ProjectTopicsField: FC<Props> = ({ control }) => {
  const [suggestions, setSuggestions] = useState<VideoTopicResource[]>([]);

  const [title, setTitle] = useState("");

  const inputRef = useRef<HTMLInputElement>(null);

  const {
    field: { onChange: setValue, value: topics },
    fieldState: { error },
  } = useController({
    name: "topics",
    control,
  });

  const debouncedTopic = useDebounce(title, 400);

  useEffect(() => {
    if (debouncedTopic.length < 2) {
      return setSuggestions([]);
    }

    api
      .get<never, VideoTopicResource[]>("/v1/video-topics?category=script", {
        params: {
          search: debouncedTopic,
        },
      })
      .then((suggestions) => setSuggestions(suggestions));
  }, [debouncedTopic]);

  const addAttachment = (topicToAdd: string) => {
    if (!topicToAdd) return;
    if (
      !topics?.find((a) => a === topics.find((topic) => topic === topicToAdd))
    ) {
      setValue([...topics, topicToAdd]);
    }

    setTitle("");
    setSuggestions([]);
  };

  const onAttachmentRemove = (topicToRemove: string) => {
    setValue(topics.filter((topic) => topic !== topicToRemove));
  };

  const splitMatchedText = (str: string) => {
    const regExp = new RegExp(debouncedTopic, "gi");
    return str.replace(regExp, (match) => `<span>${match}</span>`);
  };

  const onSuggestionClick = (suggestion: string) => {
    addAttachment(suggestion);
    inputRef.current?.focus();
  };

  const filteredSuggestions = suggestions.filter(
    (suggestion) => !topics?.map((topic) => topic).includes(suggestion.title)
  );

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;

    e.preventDefault();

    addAttachment(title);
  };

  return (
    <Larger>
      <AttachmentBox>
        <InputWrap>
          <Label>Topics</Label>
          <Input
            placeholder="Enter topic and press Enter"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            ref={inputRef}
            onKeyDown={onKeyDown}
          />
          {!!filteredSuggestions.length && (
            <Autocomplete>
              {filteredSuggestions.map((susggestion) => (
                <AutocompleteButton
                  key={susggestion.title}
                  type="button"
                  as="button"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    onSuggestionClick(susggestion.title);
                  }}
                >
                  <Text
                    $color="gray"
                    $type="captionSemibold"
                    dangerouslySetInnerHTML={{
                      __html: splitMatchedText(susggestion.title),
                    }}
                  />
                  <Text $color="gray" $type="extraSmall">
                    {!!susggestion.team &&
                      `created by ${susggestion.team.name}`}
                  </Text>
                </AutocompleteButton>
              ))}
            </Autocomplete>
          )}
        </InputWrap>
        {!!topics?.length && (
          <Tags>
            {topics.map((topic) => (
              <Tag
                key={topic}
                onDelete={(e) => {
                  e.stopPropagation();
                  onAttachmentRemove(topic);
                }}
              >
                {topic}
              </Tag>
            ))}
          </Tags>
        )}
      </AttachmentBox>
      {!!error && <ValidationMessage>{error.message}</ValidationMessage>}
    </Larger>
  );
};

export default ProjectTopicsField;
